import React, { useState, useEffect, useRef, Suspense } from 'react';
import SysOwnerSideBar from './SysOwnerSidebar';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import ErrorBoundary from '../../common/ErrorBoundery';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { useParams } from 'react-router-dom';
import appService from '../../../services/ApplicationService';
import HRService from '../../../services/HrService';
import './styles.css';
import roleService from '../../../services/RoleService';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AddLinkIcon from '@mui/icons-material/AddLink';
import Modal from '../../common/Modal';
import MultipleSelect from '../../common/MultipleSelect';
import Badge from '@mui/material/Badge';
import { styled } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SearchIcon from '@mui/icons-material/Search';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Fade from '@mui/material/Fade';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import BulkRoleAssignmentModal from '../../common/BulkRoleAssignmentModal';
import UploadIcon from '@mui/icons-material/Upload';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

const DataTable = React.lazy(() => import('../../common/DataGrid'));

// Global styles for premium appearance
const globalStyles = `
    @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.1); }
        100% { transform: scale(1); }
    }
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }
    @keyframes blink {
        0% { opacity: 0.6; }
        50% { opacity: 1; }
        100% { opacity: 0.6; }
    }
    .premium-card {
        position: relative;
        isolation: isolate;
        overflow: hidden;
    }
    .premium-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, #42a5f5, #1976d2);
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 1;
    }
    .premium-card:hover::before {
        opacity: 1;
    }
`;

// Custom styled components
const RoleBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: 8,
    top: 8,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
    backgroundColor: theme.palette.error.main,
    color: 'white',
    fontWeight: 'bold',
    minWidth: '20px',
    height: '20px',
  },
}));

const RoleCard = styled(Card)(({ theme, assigned }) => ({
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  height: '100%',
  borderLeft: assigned ? `4px solid ${theme.palette.primary.main}` : `4px solid ${theme.palette.grey[400]}`,
  animation: 'fadeIn 0.5s ease-out',
  position: 'relative',
  overflow: 'hidden',
  backgroundImage: 'linear-gradient(to bottom, #ffffff, #fafbfd)',
  boxShadow: 'rgba(0, 0, 0, 0.03) 0px 2px 8px, rgba(0, 0, 0, 0.01) 0px 0px 0px 1px',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: -1,
    opacity: 0,
    transition: 'opacity 0.3s ease',
    background: 'linear-gradient(145deg, rgba(255,255,255,0.4) 0%, rgba(240,249,255,0.2) 100%)',
  },
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: 'rgba(17, 12, 46, 0.08) 0px 8px 25px 0px',
    backgroundImage: 'linear-gradient(to bottom, #ffffff, #f5f9ff)',
    '&::after': {
      opacity: 1,
    },
  },
}));

const HeaderSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: 'linear-gradient(to right, #f8fafc, #ffffff)',
  boxShadow: 'none',
  borderRadius: '10px',
}));

const ContentContainer = styled(mui.Box)(({ theme }) => ({
  backgroundColor: '#f8fafc',
  borderRadius: 12,
  minHeight: '80vh',
  boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
  padding: theme.spacing(2),
}));

const ActionButton = styled(mui.Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  boxShadow: 'none',
  padding: '8px 16px',
  fontWeight: 600,
  transition: 'all 0.2s',
  '&:hover': {
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    transform: 'translateY(-2px)',
  },
}));

const PremiumTextField = styled(mui.TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    transition: 'all 0.2s',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    '&:hover': {
      backgroundColor: '#ffffff',
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
    }
  }
}));

const ViewToggleButton = styled(mui.Button)(({ theme }) => ({
  minWidth: 40,
  width: 40,
  height: 40,
  padding: 0,
  borderRadius: 8,
}));

const RoleMatrix = () => {
    const [app, setApp] = useState([]);
    const [roles, setRoles] = useState([]);
    const [roleOwnerData, setRoleOwnerdata] = useState([]);
    const [error, setError] = useState(null);
    const { id } = useParams();
    const [roleOwnerList, setRoleOwnerList] = useState([]);
    const [selectedOwners, setSelectedOwners] = useState({});
    const [assignedOwner, setAssignedOwner] = useState({});
    const [selectedRole, setSelectedRole] = useState({});
    const [saved, setSaved] = useState(false);
    const [unassignedCount, setunassignedCount] = useState(0);
    const [roleData, setRoleData] = useState({});
    const [openMappingModal, setOpenMappingModal] = useState(false);
  const [mappedRoles, setMappedRoles] = useState([]);
  const [activeView, setActiveView] = useState('assigned'); // 'assigned' or 'unassigned'
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [openBulkUploadModal, setOpenBulkUploadModal] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const isRoleOwnerCreatedRef = useRef(false);

    const handleOpenMappingModal = () => {
        setOpenMappingModal(true);
    };

    const handleCloseMappingModal = (event, reason) => {
        if (reason == 'backdropClick') {
            setOpenMappingModal(true)
        }
        else {
            setOpenMappingModal(false);
        }
    };

  const handleOpenBulkUploadModal = () => {
    setOpenBulkUploadModal(true);
  };

  const handleCloseBulkUploadModal = () => {
    setOpenBulkUploadModal(false);
  };

  const handleBulkUploadSuccess = (count) => {
    console.log(`Successfully processed ${count} role assignments`);
    // Force a refresh of the roles data to reflect new assignments
    setSaved(true);
    setOpenBulkUploadModal(false);
    
    // Optionally show a success message
    setSuccessMessage(`Successfully processed ${count} role assignments`);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleOpenConfirmDialog = () => {
    setOpenConfirmDialog(true);
  };
  
  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleConfirmAssignment = () => {
    // Proceed with the assignment
    saveChanges();
    handleCloseConfirmDialog();
    
    // Show success message
    const isNewAssignment = selectedRole.isUnassigned;
    const ownerName = selectedOwners.label ? selectedOwners.label.split('(')[0].trim() : 'unknown user';
    const roleName = selectedRole.ROLE_NAME || selectedRole.value;
    
    setSuccessMessage(
      isNewAssignment
        ? `Successfully assigned ${ownerName} as owner of role "${roleName}"`
        : `Successfully updated owner of role "${roleName}" to ${ownerName}`
    );
    
    // Clear message after 6 seconds
    setTimeout(() => setSuccessMessage(''), 6000);
  };

  useEffect(() => {
    // Add global styles
    const style = document.createElement('style');
    style.textContent = globalStyles;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
        setLoading(true);
                const existingRoleOwners = await roleService.fetchRoleOwnersByApp(id);

        setMappedRoles(existingRoleOwners);
        
                if (isRoleOwnerCreatedRef.current) {
                    console.log('Role owners have already been created. Skipping...');
          return;
                }

                const roles = await appService.fetchAppsRecordById(id);
                const existingRoleNames = new Set(existingRoleOwners.map(owner => owner.ROLE_NAME));
                const filteredRoles = roles.filter(role => !existingRoleNames.has(role.ROLE_NAME));

                if (Array.isArray(filteredRoles)) {
                    const roleNames = [...new Set(filteredRoles.map(item => item.ROLE_NAME))];
                    const formattedRoleNames = roleNames.map(item => ({
                        value: item,
                        label: item,
                    }));

          setunassignedCount(formattedRoleNames.length);
                    setRoles(formattedRoleNames);
                }
        setLoading(false);
            } catch (error) {
                console.error('Error fetching roles:', error);
        setLoading(false);
            }
        };

        const fetchApp = async () => {
            try {
                const app = await appService.fetchAppsById(id);
                setApp(app);
            } catch (error) {
                console.error('Error fetching applist:', error);
            }
        };

        const fetchRoleOwner = async () => {
            try {
                // Get all HR records and filter for active employees
                const hrList = await HRService.fetchAllHRRecords();
                if (hrList) {
                    // Filter for active employees
                    const activeHrRecords = hrList.filter(record => 
                      record.STATUS && record.STATUS.toLowerCase() === 'active'
                    );
                    
                    console.log(`Found ${activeHrRecords.length} active employees for role owner dropdown`);

                    // Format them with proper dropdown structure - same format as BulkRoleAssignmentUpload
                    const formattedRoleOwners = activeHrRecords.map(item => ({
                        value: item.id,
                        label: `${item.FIRST_NAME} ${item.LAST_NAME}  (${item.EMAIL_ADDRESS})`,
                        firstName: item.FIRST_NAME,
                        lastName: item.LAST_NAME,
                        email: item.EMAIL_ADDRESS
                    }));

                    setRoleOwnerList(formattedRoleOwners);
                }
            } catch (error) {
                console.error('Error fetching role owner:', error);
            }
        };

        if (id || saved) {
            fetchRoles();
            fetchApp();
            fetchRoleOwner();

            if (saved) {
                setSaved(false);
            }
        }
  }, [saved]);

  const handleRoleOwnerSelect = (selectedOptions) => {
    const roleowner = selectedOptions.value;
    setAssignedOwner(roleowner);
    setSelectedOwners(selectedOptions);

    const updatedData = {
      ...roleOwnerData,
      ROLE_OWNERS: roleowner
    };

    setRoleData(updatedData);
  };

  const saveChanges = async () => {
    try {
      const recordExist = await roleService.fetchRoleOwnerById(id, selectedRole.ROLE_NAME);

      if (recordExist && recordExist.length > 0) {
        await roleService.updateRoleOwner(recordExist[0].id, roleData);
      } else {
        await roleService.createRoleOwner(roleData);
      }

      // After saving, check if all roles now have owners and update the ROLE_OWNER_SETUP flag
      try {
        // Get the app details
        const appDetails = await appService.fetchAppsById(id);
        console.log('App details fetched for ROLE_OWNER_SETUP update:', appDetails);
        
        // Get all role owners for this app and all roles
        const existingRoleOwners = await roleService.fetchRoleOwnersByApp(id);
        console.log('Existing role owners:', existingRoleOwners.length);
        
        const allRoles = await appService.fetchAppsRecordById(id);
        console.log('All roles for this app:', allRoles.length);
        
        // Extract role names from both arrays
        const roleNames = allRoles.map(role => role.ROLE_NAME);
        const assignedRoleNames = existingRoleOwners.map(owner => owner.ROLE_NAME);
        
        console.log('Role names:', roleNames);
        console.log('Assigned role names:', assignedRoleNames);
        
        // Check if all roles have owners
        const allRolesAssigned = roleNames.every(roleName => 
          assignedRoleNames.includes(roleName)
        );
        console.log('All roles assigned?', allRolesAssigned);
        console.log('Current ROLE_OWNER_SETUP value:', appDetails.ROLE_OWNER_SETUP);
        
        // Update the ROLE_OWNER_SETUP flag if needed
        if (appDetails && appDetails.id && allRolesAssigned !== appDetails.ROLE_OWNER_SETUP) {
          console.log('Updating ROLE_OWNER_SETUP from', appDetails.ROLE_OWNER_SETUP, 'to', allRolesAssigned);
          const updateResult = await appService.updateApp(appDetails.id, {
            ...appDetails,
            ROLE_OWNER_SETUP: allRolesAssigned
          });
          console.log('Result of updating ROLE_OWNER_SETUP:', updateResult);
          console.log(`Updated ROLE_OWNER_SETUP to ${allRolesAssigned} for app ${id}`);
        } else if (appDetails && appDetails.id) {
          console.log('ROLE_OWNER_SETUP already has the correct value:', allRolesAssigned);
        } else {
          console.error('App details object is invalid or missing ID:', appDetails);
        }
      } catch (error) {
        console.error('Error updating ROLE_OWNER_SETUP flag:', error);
        if (error.response) {
          console.error('Error response data:', error.response.data);
          console.error('Error response status:', error.response.status);
        }
        // Continue with the success flow even if this update fails
      }

      setSaved(true);
      setSelectedOwners([]);
      handleCloseMappingModal();
    } catch (error) {
      console.error('Error updating record:', error);
    }
  };

  const handleMappingClick = (role) => {
        setSelectedRole(role);

        let data = {
            APP_NAME: '',
            ROLE_NAME: '',
            ...Object.keys(roleOwnerData).reduce((acc, key) => {
                acc[key] = '';
                return acc;
            }, {}),
        };

        data = {
            ...data,
            APP_NAME: id,
      ROLE_NAME: role.ROLE_NAME || role.value,
    };

    setRoleOwnerdata(data);
    setOpenMappingModal(true);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const getAssigneeInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`;
    }
    return parts[0][0];
  };

  const getRandomColor = (str) => {
    if (!str) return '#757575';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = ['#1976d2', '#1e88e5', '#2196f3', '#42a5f5', '#64b5f6', '#90caf9', '#bbdefb'];
    return colors[Math.abs(hash) % colors.length];
  };

  // Filter roles based on search term
  const filteredMappedRoles = mappedRoles.filter(role => 
    role.ROLE_NAME.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUnassignedRoles = roles.filter(role => 
    role.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format unassigned roles to match the structure
  const formattedUnassignedRoles = filteredUnassignedRoles.map(role => ({
    ROLE_NAME: role.value,
    isUnassigned: true
  }));

  const theme = createTheme({
    palette: {
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#42a5f5',
      },
      success: {
        main: '#4caf50',
      },
      warning: {
        main: '#ff9800',
      },
      background: {
        default: '#f8fafc',
      },
      text: {
        primary: '#1e293b',
        secondary: '#475569',
      },
    },
    typography: {
      fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
      h4: {
        fontWeight: 600,
      },
      h5: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 600,
      },
      subtitle1: {
        fontWeight: 600,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
    },
  });

  // Prepare display data based on active view
  const displayRoles = activeView === 'assigned' ? filteredMappedRoles : formattedUnassignedRoles;

  const renderListView = () => {
    // Function to get an icon based on the role name
    const getRoleIcon = (roleName) => {
      const roleLower = roleName.toLowerCase();
      if (roleLower.includes('admin')) {
        return <AdminPanelSettingsIcon sx={{ color: 'primary.main', fontSize: 20, mr: 1.5 }} />;
      } else if (roleLower.includes('security') || roleLower.includes('audit')) {
        return <SecurityIcon sx={{ color: 'primary.main', fontSize: 20, mr: 1.5 }} />;
      } else if (roleLower.includes('access') || roleLower.includes('key') || roleLower.includes('permission')) {
        return <VpnKeyIcon sx={{ color: 'primary.main', fontSize: 20, mr: 1.5 }} />;
      } else {
        return <SupervisorAccountIcon sx={{ color: 'primary.main', fontSize: 20, mr: 1.5 }} />;
      }
    };

    return (
      <TableContainer component={mui.Paper} sx={{ boxShadow: 'none', backgroundColor: 'transparent' }}>
        <Table sx={{ minWidth: 650 }} aria-label="role table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>Role Name</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>Owner</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }} align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRoles.map((role, index) => {
              const matchedOwner = role.isUnassigned 
                ? null 
                : roleOwnerList.find(owner => role.ROLE_OWNERS === owner.value);

              const ownerName = matchedOwner 
                ? `${matchedOwner.firstName} ${matchedOwner.lastName}` 
                : null;

              return (
                <TableRow
                  key={index}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                    transition: 'background-color 0.2s',
                    animation: `fadeIn 0.5s ease-out ${index * 0.05}s`
                  }}
                >
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getRoleIcon(role.ROLE_NAME)}
                      <mui.Typography sx={{ fontWeight: 500 }}>
                        {role.ROLE_NAME}
                      </mui.Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={role.isUnassigned ? "Unassigned" : "Assigned"} 
                      color={role.isUnassigned ? "default" : "primary"} 
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: '12px', fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    {!role.isUnassigned && ownerName ? (
                      <Box display="flex" alignItems="center">
                        <Avatar 
                          sx={{ 
                            bgcolor: getRandomColor(ownerName),
                            width: 28, 
                            height: 28,
                            mr: 1.5,
                            fontSize: '0.8rem'
                          }}
                        >
                          {getAssigneeInitials(ownerName)}
                        </Avatar>
                        <mui.Typography variant="body2">
                          {ownerName}
                        </mui.Typography>
                      </Box>
                    ) : (
                      <mui.Typography variant="body2" color="text.secondary">
                        None
                      </mui.Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <mui.Button
                      size="small"
                      color="primary"
                      onClick={() => handleMappingClick(role)}
                      endIcon={<ArrowForwardIcon />}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 600,
                      }}
                    >
                      {role.isUnassigned ? "Assign Owner" : "Update Owner"}
                    </mui.Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {displayRoles.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                  <mui.Typography variant="h6" color="text.secondary" gutterBottom>
                    No roles found
                  </mui.Typography>
                  <mui.Typography variant="body2" color="text.secondary">
                    {searchTerm 
                      ? "Try adjusting your search criteria" 
                      : activeView === 'unassigned' 
                        ? "All roles have been assigned" 
                        : "No roles have been assigned yet"}
                  </mui.Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderGridView = () => {
    return (
      <Grid container spacing={3}>
        {displayRoles.map((role, index) => {
          const matchedOwner = role.isUnassigned 
            ? null 
            : roleOwnerList.find(owner => role.ROLE_OWNERS === owner.value);

          const ownerName = matchedOwner 
            ? `${matchedOwner.firstName} ${matchedOwner.lastName}` 
            : null;

          return (
            <Grid item xs={12} sm={6} md={4} key={index} sx={{ animation: `fadeIn 0.5s ease-out ${index * 0.05}s` }}>
              <RoleCard 
                assigned={!role.isUnassigned}
                className="premium-card"
              >
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Chip 
                      label={role.isUnassigned ? "Unassigned" : "Assigned"} 
                      color={role.isUnassigned ? "default" : "primary"} 
                      size="small"
                      variant="outlined"
                      sx={{ 
                        borderRadius: '12px', 
                        fontWeight: 500,
                        px: 1
                      }}
                    />
                  </Box>
                  
                  <mui.Typography 
                    variant="subtitle1" 
                    fontWeight={600} 
                    sx={{ 
                      mb: 2,
                      color: 'text.primary',
                      fontSize: '1rem',
                    }}
                  >
                    {role.ROLE_NAME}
                  </mui.Typography>
                  
                  {!role.isUnassigned && ownerName && (
                    <Box display="flex" alignItems="center" mt="auto">
                      <Avatar 
                        sx={{ 
                          bgcolor: getRandomColor(ownerName),
                          width: 40, 
                          height: 40,
                          mr: 1.5
                        }}
                      >
                        {getAssigneeInitials(ownerName)}
                      </Avatar>
                      <Box>
                        <mui.Typography variant="body2" fontWeight={500}>
                          {ownerName}
                        </mui.Typography>
                        {matchedOwner && (
                          <mui.Typography variant="caption" color="text.secondary">
                            {matchedOwner.email}
                          </mui.Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  
                  {role.isUnassigned && (
                    <Box display="flex" alignItems="center" mt="auto">
                      <mui.Avatar 
                        sx={{ bgcolor: '#e0e0e0', width: 40, height: 40, mr: 1.5 }}
                      >
                        <PersonIcon color="action" />
                      </mui.Avatar>
                      <mui.Typography variant="body2" color="text.secondary">
                        No owner assigned
                      </mui.Typography>
                    </Box>
                  )}
                  
                  <Box 
                    sx={{ 
                      mt: 2.5,
                      pt: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      color: 'primary.main',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      borderTop: '1px solid',
                      borderColor: 'rgba(0, 0, 0, 0.04)',
                    }}
                  >
                    <mui.Button
                      size="small"
                      onClick={() => handleMappingClick(role)}
                      endIcon={<ArrowForwardIcon className="arrow" sx={{ 
                        transition: 'transform 0.2s ease-in-out',
                        '.MuiButton-root:hover &': {
                          transform: 'translateX(4px)'
                        }
                      }} />}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 600,
                        p: 0,
                        '&:hover': {
                          background: 'transparent'
                        }
                      }}
                    >
                      {role.isUnassigned ? "Assign Owner" : "Update Owner"}
                    </mui.Button>
                  </Box>
                </CardContent>
              </RoleCard>
            </Grid>
          );
        })}
        
        {displayRoles.length === 0 && (
          <Grid item xs={12}>
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center" 
              p={6}
            >
              <mui.Typography variant="h6" color="text.secondary" gutterBottom>
                No roles found
              </mui.Typography>
              <mui.Typography variant="body2" color="text.secondary">
                {searchTerm 
                  ? "Try adjusting your search criteria" 
                  : activeView === 'unassigned' 
                    ? "All roles have been assigned" 
                    : "No roles have been assigned yet"}
              </mui.Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    );
    };

    const customMainContent = (
    <ThemeProvider theme={theme}>
        <div>
            <ResponsiveContainer>
          <mui.Box sx={{ padding: '24px' }}>
                <mui.Breadcrumbs aria-label="breadcrumb">
                    <mui.Link underline="hover" color="inherit" href="/dashboard">
                        <i className="material-icons">home</i>
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/dashboard">
                        My Dashboard
                    </mui.Link>
              <mui.Typography color="text.primary">Role Matrix</mui.Typography>
              <mui.Typography color="text.primary">{app?.APP_NAME}</mui.Typography>
                </mui.Breadcrumbs>

            <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 3 }}>
              <VerifiedUserIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
              <mui.Box>
                <mui.Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                  {app?.APP_NAME} Role Ownership
                </mui.Typography>
                <mui.Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Manage role ownership and assign responsibilities across your application
                </mui.Typography>
              </mui.Box>
            </mui.Box>

            <ContentContainer>
              <mui.Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" gap={2}>
                  <ActionButton
                    variant={activeView === 'assigned' ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={() => setActiveView('assigned')}
                    startIcon={<VerifiedUserIcon />}
                    sx={{ position: 'relative' }}
                  >
                    Assigned Roles
                  </ActionButton>
                  <ActionButton
                    variant={activeView === 'unassigned' ? 'contained' : 'outlined'}
                    color="inherit"
                    onClick={() => setActiveView('unassigned')}
                    startIcon={<PersonIcon />}
                    sx={{ position: 'relative' }}
                  >
                    Unassigned
                    {unassignedCount > 0 && (
                      <mui.Box
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          backgroundColor: theme.palette.error.main,
                          color: 'white',
                          borderRadius: '50%',
                          width: 22,
                          height: 22,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          border: '2px solid white',
                        }}
                      >
                        {unassignedCount}
                      </mui.Box>
                    )}
                  </ActionButton>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box>
                    <PremiumTextField
                      placeholder="Search roles..."
                      size="small"
                      value={searchTerm}
                      onChange={handleSearch}
                      InputProps={{
                        startAdornment: (
                          <mui.InputAdornment position="start">
                            <SearchIcon sx={{ color: 'text.secondary' }} />
                          </mui.InputAdornment>
                        ),
                      }}
                      sx={{ width: '100%', maxWidth: '250px' }}
                    />
                  </Box>
                  <mui.Box sx={{ display: 'flex', gap: 1 }}>
                    <ViewToggleButton 
                      variant={viewMode === 'grid' ? 'contained' : 'outlined'}
                      onClick={() => setViewMode('grid')}
                      color={viewMode === 'grid' ? 'primary' : 'inherit'}
                      title="Grid View"
                    >
                      <GridViewIcon fontSize="small" />
                    </ViewToggleButton>
                    <ViewToggleButton 
                      variant={viewMode === 'list' ? 'contained' : 'outlined'}
                      onClick={() => setViewMode('list')}
                      color={viewMode === 'list' ? 'primary' : 'inherit'}
                      title="List View"
                    >
                      <ViewListIcon fontSize="small" />
                    </ViewToggleButton>
                  </mui.Box>
                  <mui.Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={handleOpenBulkUploadModal}
                    size="small"
                    sx={{ 
                      textTransform: 'none',
                      borderRadius: '8px',
                      ml: 1
                    }}
                  >
                    Bulk Assign/Edit
                  </mui.Button>
                </Box>
              </mui.Box>

              {loading ? (
                <Box display="flex" justifyContent="center" my={6}>
                  <mui.CircularProgress 
                    size={40} 
                    thickness={4}
                    sx={{
                      color: 'primary.main',
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                  />
                  <mui.Typography 
                    variant="body1" 
                    sx={{ 
                      ml: 2, 
                      color: 'text.secondary',
                      fontWeight: 500
                    }}
                  >
                    Loading roles...
                  </mui.Typography>
                </Box>
              ) : (
                <Fade in={!loading}>
                  <Box>
                    {viewMode === 'grid' ? renderGridView() : renderListView()}
                  </Box>
                </Fade>
              )}
            </ContentContainer>

                <Modal
                    open={openMappingModal}
              size={'md'}
                    onClose={handleCloseMappingModal}
                    body={
                <Box sx={{ pt: 2, pb: 3 }}>
                  <mui.Typography 
                    variant="h5" 
                    fontWeight={600} 
                    sx={{ 
                      mb: 1,
                      px: 3, 
                      color: 'text.primary',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <VerifiedUserIcon color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
                    {selectedRole.isUnassigned ? "Assign Role Owner" : "Update Role Owner"}
                  </mui.Typography>
                  
                  <mui.Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{
                      mb: 3,
                      px: 3
                    }}
                  >
                    Select an owner for the role: <strong>{selectedRole.ROLE_NAME || selectedRole.value}</strong>
                  </mui.Typography>
                  
                  <mui.Divider sx={{ mb: 3 }} />
                  
                  <Box px={3}>
                    <mui.Typography 
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Role Details
                    </mui.Typography>
                    
                    <mui.Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 3,
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        borderRadius: 2
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <mui.Typography variant="caption" color="text.secondary">
                            Application
                          </mui.Typography>
                          <mui.Typography variant="body2" fontWeight={500}>
                            {app?.APP_NAME}
                          </mui.Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <mui.Typography variant="caption" color="text.secondary">
                            Role Name
                          </mui.Typography>
                          <mui.Typography variant="body2" fontWeight={500}>
                            {selectedRole.ROLE_NAME || selectedRole.value}
                          </mui.Typography>
                        </Grid>
                      </Grid>
                    </mui.Paper>

                    {/* Show current owner if role is already assigned */}
                    {!selectedRole.isUnassigned && (
                      <>
                        <mui.Typography 
                          variant="subtitle2"
                          sx={{
                            mb: 1.5,
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          Current Owner
                        </mui.Typography>

                        <mui.Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            mb: 3,
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            border: '1px solid rgba(25, 118, 210, 0.1)',
                            borderRadius: 2
                          }}
                        >
                          {(() => {
                            const matchedOwner = roleOwnerList.find(owner => 
                              selectedRole.ROLE_OWNERS === owner.value
                            );

                            if (matchedOwner) {
                              return (
                                <Box display="flex" alignItems="center">
                                  <Avatar
                                    sx={{
                                      bgcolor: getRandomColor(`${matchedOwner.firstName} ${matchedOwner.lastName}`),
                                      width: 40,
                                      height: 40,
                                      mr: 2
                                    }}
                                  >
                                    {getAssigneeInitials(`${matchedOwner.firstName} ${matchedOwner.lastName}`)}
                                  </Avatar>
                                  <Box>
                                    <mui.Typography variant="body2" fontWeight={600}>
                                      {matchedOwner.firstName} {matchedOwner.lastName}
                                    </mui.Typography>
                                    <mui.Typography variant="caption" color="text.secondary">
                                      {matchedOwner.email}
                                    </mui.Typography>
                                  </Box>
                                </Box>
                              );
                            } else {
                              return (
                                <mui.Typography variant="body2" color="text.secondary">
                                  Unable to find details for the current owner. They may have been removed from the system.
                                </mui.Typography>
                              );
                            }
                          })()}
                        </mui.Paper>
                      </>
                    )}
                    
                    <mui.Typography 
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {selectedRole.isUnassigned ? "Owner Assignment" : "Change Owner"}
                    </mui.Typography>
                    
                    <mui.Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      The selected person will be responsible for this role and receive notifications related to it.
                            </mui.Typography>

                    <Box
                      sx={{
                        backgroundColor: '#fff',
                        borderRadius: 2,
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        p: 0.5,
                        mb: 2
                      }}
                    >
                            <MultipleSelect
                                isMultiSelect={false}
                        placeholderText="Search and select a role owner..."
                                selectedOptions={selectedOwners}
                                selectOptions={roleOwnerList}
                                value={selectedOwners}
                                handleChange={handleRoleOwnerSelect}
                            />
                    </Box>
                    
                    {selectedOwners && selectedOwners.value && selectedOwners.label && (
                      <Fade in={true}>
                        <mui.Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            mb: 1,
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            border: '1px solid rgba(25, 118, 210, 0.1)',
                            borderRadius: 2,
                            borderLeft: '4px solid',
                            borderLeftColor: 'primary.main',
                          }}
                        >
                          <Box display="flex" alignItems="center">
                            <Avatar
                              sx={{
                                bgcolor: getRandomColor(selectedOwners.label || ''),
                                width: 40,
                                height: 40,
                                mr: 2
                              }}
                            >
                              {getAssigneeInitials(selectedOwners.label || '')}
                            </Avatar>
                            <Box>
                              <mui.Typography variant="body2" fontWeight={600}>
                                {(selectedOwners.label || '').split('(')[0].trim()}
                              </mui.Typography>
                              <mui.Typography variant="caption" color="text.secondary">
                                {(selectedOwners.label || '').match(/\((.*?)\)/)?.[1] || ''}
                              </mui.Typography>
                            </Box>
                          </Box>
                        </mui.Paper>
                      </Fade>
                    )}
                  </Box>
                </Box>
                    }
                    footer={
                <Box display="flex" justifyContent="flex-end" gap={1.5} p={2}>
                  <mui.Button 
                    onClick={handleCloseMappingModal} 
                    variant="outlined"
                    color="inherit"
                    sx={{ 
                      borderRadius: 2,
                      px: 2
                    }}
                  >
                                Cancel
                            </mui.Button>
                  <mui.Button 
                    onClick={handleOpenConfirmDialog} 
                    variant="contained" 
                    color="primary"
                    disabled={!selectedOwners || !selectedOwners.value}
                    disableElevation
                    sx={{ 
                      borderRadius: 2,
                      px: 3
                    }}
                  >
                    {selectedRole.isUnassigned ? "Assign Owner" : "Update Owner"}
                            </mui.Button>
                </Box>
              }
            />

            <BulkRoleAssignmentModal
              open={openBulkUploadModal}
              onClose={handleCloseBulkUploadModal}
              appId={id}
              unassignedRoles={roles}
              mappedRoles={mappedRoles}
              onSuccess={handleBulkUploadSuccess}
            />
          </mui.Box>
            </ResponsiveContainer>
        </div>
    </ThemeProvider>
  );

    return (
        <ErrorBoundary fallback={<p>Failed to load data.</p>} error={error}>
      <Suspense fallback={
        <mui.Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            backgroundColor: '#f8fafc'
          }}
        >
          <mui.Box sx={{ textAlign: 'center' }}>
            <mui.CircularProgress 
              size={40} 
              thickness={4}
              sx={{
                color: 'primary.main',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
            <mui.Typography 
              variant="body1" 
              sx={{ 
                mt: 2, 
                color: 'text.secondary',
                fontWeight: 500
              }}
            >
              Loading Role Matrix...
            </mui.Typography>
          </mui.Box>
        </mui.Box>
      }>
        <div>
          <SysOwnerSideBar mainContent={customMainContent} />
          
          {/* Success Message Snackbar */}
          <Snackbar 
            open={!!successMessage} 
            autoHideDuration={6000} 
            onClose={() => setSuccessMessage('')}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setSuccessMessage('')} 
              severity="success" 
              variant="filled"
              sx={{ 
                width: '100%',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '& .MuiAlert-icon': { fontSize: '1.25rem' },
                '& .MuiAlert-message': { fontSize: '0.95rem', fontWeight: 500 }
              }}
            >
              {successMessage}
            </Alert>
          </Snackbar>
          
          {/* Confirmation Dialog */}
          <mui.Dialog
            open={openConfirmDialog}
            onClose={handleCloseConfirmDialog}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{
              sx: { 
                borderRadius: '12px',
                maxWidth: '500px'
              }
            }}
          >
            <mui.DialogTitle id="alert-dialog-title" sx={{ pb: 1 }}>
              <mui.Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                <VerifiedUserIcon color="primary" sx={{ mr: 1, fontSize: 24 }} />
                {selectedRole.isUnassigned ? "Confirm Role Assignment" : "Confirm Role Owner Update"}
              </mui.Typography>
            </mui.DialogTitle>
            
            <mui.DialogContent>
              <mui.DialogContentText id="alert-dialog-description" component="div">
                <mui.Box sx={{ mb: 2 }}>
                  <mui.Typography variant="body2" color="text.secondary" paragraph>
                    {selectedRole.isUnassigned 
                      ? "You are about to assign a new owner to this role." 
                      : "You are about to change the owner of this role."}
                  </mui.Typography>
                  
                  <mui.Box sx={{ 
                    bgcolor: 'rgba(25, 118, 210, 0.04)', 
                    p: 2, 
                    borderRadius: 1, 
                    mb: 2, 
                    border: '1px solid rgba(25, 118, 210, 0.12)' 
                  }}>
                    <mui.Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary' }}>
                      Role Details:
                    </mui.Typography>
                    
                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <mui.Typography variant="body2" color="text.secondary">
                        Application:
                      </mui.Typography>
                      <mui.Typography variant="body2" fontWeight={500}>
                        {app?.APP_NAME}
                      </mui.Typography>
                    </mui.Box>
                    
                    <mui.Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <mui.Typography variant="body2" color="text.secondary">
                        Role Name:
                      </mui.Typography>
                      <mui.Typography variant="body2" fontWeight={500}>
                        {selectedRole.ROLE_NAME || selectedRole.value}
                      </mui.Typography>
                    </mui.Box>
                  </mui.Box>
                  
                  {!selectedRole.isUnassigned && (
                    <mui.Box sx={{ mb: 2 }}>
                      <mui.Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary' }}>
                        Current Owner:
                      </mui.Typography>
                      
                      <mui.Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: 'rgba(0, 0, 0, 0.02)', 
                        p: 1.5, 
                        borderRadius: 1,
                        border: '1px solid rgba(0, 0, 0, 0.08)'
                      }}>
                        {(() => {
                          const currentOwner = roleOwnerList.find(owner => selectedRole.ROLE_OWNERS === owner.value);
                          
                          if (currentOwner) {
                            const ownerName = `${currentOwner.firstName} ${currentOwner.lastName}`;
                            return (
                              <>
                                <Avatar 
                                  sx={{ 
                                    bgcolor: getRandomColor(ownerName),
                                    width: 32, 
                                    height: 32,
                                    mr: 1.5,
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  {getAssigneeInitials(ownerName)}
                                </Avatar>
                                <mui.Box>
                                  <mui.Typography variant="body2" fontWeight={500}>
                                    {ownerName}
                                  </mui.Typography>
                                  <mui.Typography variant="caption" color="text.secondary">
                                    {currentOwner.email}
                                  </mui.Typography>
                                </mui.Box>
                              </>
                            );
                          }
                          
                          return (
                            <mui.Typography variant="body2" color="text.secondary">
                              No current owner found
                            </mui.Typography>
                          );
                        })()}
                      </mui.Box>
                    </mui.Box>
                  )}
                  
                  <mui.Box sx={{ mb: 1 }}>
                    <mui.Typography variant="subtitle2" sx={{ mb: 1, color: 'text.primary' }}>
                      {selectedRole.isUnassigned ? "New Owner:" : "Change To:"}
                    </mui.Typography>
                    
                    <mui.Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      bgcolor: 'rgba(25, 118, 210, 0.04)', 
                      p: 1.5, 
                      borderRadius: 1,
                      border: '1px solid rgba(25, 118, 210, 0.12)'
                    }}>
                      {selectedOwners && selectedOwners.label ? (
                        <>
                          <Avatar 
                            sx={{ 
                              bgcolor: getRandomColor(selectedOwners.label || ''),
                              width: 32, 
                              height: 32,
                              mr: 1.5,
                              fontSize: '0.8rem'
                            }}
                          >
                            {getAssigneeInitials(selectedOwners.label || '')}
                          </Avatar>
                          <mui.Box>
                            <mui.Typography variant="body2" fontWeight={500}>
                              {(selectedOwners.label || '').split('(')[0].trim()}
                            </mui.Typography>
                            <mui.Typography variant="caption" color="text.secondary">
                              {(selectedOwners.label || '').match(/\((.*?)\)/)?.[1] || ''}
                            </mui.Typography>
                          </mui.Box>
                        </>
                      ) : (
                        <mui.Typography variant="body2" color="text.secondary">
                          No owner selected
                        </mui.Typography>
                      )}
                    </mui.Box>
                  </mui.Box>
                </mui.Box>
              </mui.DialogContentText>
            </mui.DialogContent>
            
            <mui.DialogActions sx={{ px: 3, pb: 3 }}>
              <mui.Button 
                onClick={handleCloseConfirmDialog} 
                variant="outlined"
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                Cancel
              </mui.Button>
              <mui.Button 
                onClick={handleConfirmAssignment} 
                variant="contained" 
                color="primary"
                autoFocus
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                {selectedRole.isUnassigned ? "Confirm Assignment" : "Confirm Update"}
              </mui.Button>
            </mui.DialogActions>
          </mui.Dialog>
        </div>
      </Suspense>
    </ErrorBoundary>
  );
};

export default RoleMatrix;
